/**
 * Contact Form 7 newsletter form UX helpers (validation polish + submit state).
 */

/** @type {WeakSet<HTMLElement>} */
const boundWraps = new WeakSet();

/**
 * @typedef {Object} NewsletterWrapState
 * @property {string}                             label         Original submit label.
 * @property {ReturnType<typeof setTimeout>|null} busyTimer     Failsafe busy timer.
 * @property {ReturnType<typeof setTimeout>|null} cooldownTimer Cooldown timer.
 */

/** @type {WeakMap<HTMLElement, NewsletterWrapState>} */
const wrapState = new WeakMap();

/** Failsafe so a dropped CF7 request cannot leave the button disabled forever. */
const BUSY_FAILSAFE_MS = 20000;

/**
 * Get or create per-wrapper mutable state.
 *
 * @param {HTMLElement} wrap Wrapper element.
 * @return {NewsletterWrapState}
 */
function getState( wrap ) {
	let state = wrapState.get( wrap );

	if ( ! state ) {
		state = {
			busyTimer: null,
			cooldownTimer: null,
			label: '',
		};
		wrapState.set( wrap, state );
	}

	return state;
}

/**
 * Read the visible submit label from a button.
 *
 * @param {HTMLButtonElement|HTMLInputElement} button Submit control.
 * @return {string}
 */
function readButtonLabel( button ) {
	if ( button instanceof HTMLInputElement ) {
		return button.value || '';
	}

	const label = button.querySelector( 'span' );
	return label ? label.textContent || '' : button.textContent || '';
}

/**
 * Write the visible submit label onto a button.
 *
 * @param {HTMLButtonElement|HTMLInputElement} button Submit control.
 * @param {string}                             text   Label text.
 */
function writeButtonLabel( button, text ) {
	if ( button instanceof HTMLInputElement ) {
		button.value = text;
		return;
	}

	const label = button.querySelector( 'span' );
	if ( label ) {
		label.textContent = text;
		return;
	}

	button.textContent = text;
}

/**
 * Collect submit controls inside a newsletter wrapper.
 *
 * @param {HTMLElement} wrap Wrapper element.
 * @return {Array<HTMLButtonElement|HTMLInputElement>}
 */
function getSubmitButtons( wrap ) {
	return Array.from(
		wrap.querySelectorAll(
			'.wpcf7-submit, .alteia-newsletter-form__submit'
		)
	).filter(
		( button ) =>
			button instanceof HTMLButtonElement ||
			button instanceof HTMLInputElement
	);
}

/**
 * Interactive fields that should lock during cooldown (per form instance).
 *
 * @param {HTMLElement} wrap Wrapper element.
 * @return {Array<HTMLInputElement|HTMLButtonElement>}
 */
function getCooldownFields( wrap ) {
	return Array.from(
		wrap.querySelectorAll(
			'input[type="email"], input.wpcf7-email, .wpcf7-acceptance input[type="checkbox"], .alteia-newsletter-form__consent input[type="checkbox"]'
		)
	).filter(
		( field ) =>
			field instanceof HTMLInputElement ||
			field instanceof HTMLButtonElement
	);
}

/**
 * Lock or unlock email + consent during cooldown without affecting CF7 markup.
 *
 * @param {HTMLElement} wrap   Wrapper element.
 * @param {boolean}     locked Whether fields should be disabled.
 */
function setFieldsLocked( wrap, locked ) {
	getCooldownFields( wrap ).forEach( ( field ) => {
		field.disabled = locked;
		field.setAttribute( 'aria-disabled', locked ? 'true' : 'false' );
	} );
}

/**
 * Ensure the submit button is wide enough for both idle and busy labels.
 *
 * @param {HTMLElement} wrap Wrapper element.
 */
function stabilizeSubmitWidth( wrap ) {
	const buttons = getSubmitButtons( wrap );
	const busyLabel = wrap.dataset.alteiaSubscribingLabel || 'Subscribing…';

	buttons.forEach( ( button ) => {
		if ( button.dataset.alteiaWidthStabilized === '1' ) {
			return;
		}

		const idleLabel = readButtonLabel( button ).trim() || 'Subscribe';
		const previousMinWidth = button.style.minWidth;
		const measured = Math.max(
			button.getBoundingClientRect().width,
			measureLabelWidth( button, idleLabel ),
			measureLabelWidth( button, busyLabel )
		);

		if ( measured > 0 ) {
			button.style.minWidth = `${ Math.ceil( measured ) }px`;
		} else {
			button.style.minWidth = previousMinWidth;
		}

		button.dataset.alteiaWidthStabilized = '1';
	} );
}

/**
 * Measure how wide a button would be with a given label.
 *
 * @param {HTMLButtonElement|HTMLInputElement} button Submit control.
 * @param {string}                             label  Candidate label.
 * @return {number}
 */
function measureLabelWidth( button, label ) {
	const original = readButtonLabel( button );
	writeButtonLabel( button, label );
	const width = button.getBoundingClientRect().width;
	writeButtonLabel( button, original );
	return width;
}

/**
 * Mark a newsletter CF7 wrapper as submitting / idle.
 *
 * @param {HTMLElement} wrap Wrapper element.
 * @param {boolean}     busy Whether submission is in progress.
 */
function setBusy( wrap, busy ) {
	const state = getState( wrap );
	const buttons = getSubmitButtons( wrap );
	const subscribingLabel =
		wrap.dataset.alteiaSubscribingLabel || 'Subscribing…';

	wrap.classList.toggle( 'is-submitting', busy );

	if ( state.busyTimer ) {
		clearTimeout( state.busyTimer );
		state.busyTimer = null;
	}

	buttons.forEach( ( button ) => {
		if ( busy ) {
			if ( ! state.label ) {
				state.label = readButtonLabel( button ).trim() || 'Subscribe';
			}
			writeButtonLabel( button, subscribingLabel );
			button.disabled = true;
			button.setAttribute( 'aria-busy', 'true' );
		} else if ( ! wrap.classList.contains( 'is-cooling-down' ) ) {
			if ( state.label ) {
				writeButtonLabel( button, state.label );
			}
			button.disabled = false;
			button.setAttribute( 'aria-busy', 'false' );
		}
	} );

	if ( busy ) {
		// CF7 network failures do not always fire wpcf7submit — recover the UI.
		state.busyTimer = setTimeout( () => {
			if ( wrap.classList.contains( 'is-submitting' ) ) {
				setBusy( wrap, false );
				wrap.classList.add( 'is-invalid' );
			}
		}, BUSY_FAILSAFE_MS );
	}
}

/**
 * Show or clear a per-form cooldown status message.
 *
 * Uses a <div> (not <p>) so CF7 paragraph `display: contents` rules cannot
 * dissolve the box and drop the message into the email/button row.
 *
 * @param {HTMLElement} wrap    Wrapper element.
 * @param {string}      message Message text; empty clears the region.
 */
function setCooldownMessage( wrap, message ) {
	const form = wrap.querySelector( '.wpcf7-form' );
	if ( ! form ) {
		return;
	}

	let region = wrap.querySelector( '[data-alteia-newsletter-cooldown]' );

	if ( ! message ) {
		if ( region ) {
			region.hidden = true;
			region.textContent = '';
		}
		return;
	}

	if ( ! region ) {
		region = document.createElement( 'div' );
		region.className = 'alteia-newsletter-form__cooldown';
		region.setAttribute( 'data-alteia-newsletter-cooldown', '' );
		region.setAttribute( 'role', 'status' );
		region.setAttribute( 'aria-live', 'polite' );
		form.appendChild( region );
	}

	region.hidden = false;
	region.textContent = message;
}

/**
 * Temporarily block resubmits after a successful subscription (per form instance).
 *
 * @param {HTMLElement} wrap Wrapper element.
 */
function startCooldown( wrap ) {
	const state = getState( wrap );
	const cooldownMs = Math.max(
		0,
		parseInt( wrap.dataset.alteiaCooldownMs || '5000', 10 ) || 5000
	);
	const message =
		wrap.dataset.alteiaCooldownMessage ||
		'Please wait a few seconds before submitting again.';
	const buttons = getSubmitButtons( wrap );

	if ( state.cooldownTimer ) {
		clearTimeout( state.cooldownTimer );
		state.cooldownTimer = null;
	}

	if ( cooldownMs <= 0 ) {
		return;
	}

	wrap.classList.add( 'is-cooling-down' );
	setCooldownMessage( wrap, message );
	setFieldsLocked( wrap, true );

	buttons.forEach( ( button ) => {
		button.disabled = true;
		button.setAttribute( 'aria-busy', 'false' );
		button.setAttribute( 'aria-disabled', 'true' );
	} );

	state.cooldownTimer = setTimeout( () => {
		wrap.classList.remove( 'is-cooling-down' );
		setCooldownMessage( wrap, '' );
		setFieldsLocked( wrap, false );
		buttons.forEach( ( button ) => {
			if ( state.label ) {
				writeButtonLabel( button, state.label );
			}
			button.disabled = false;
			button.setAttribute( 'aria-disabled', 'false' );
		} );
		state.label = '';
		state.cooldownTimer = null;
	}, cooldownMs );
}

/**
 * Resolve the Alteia newsletter wrapper for a CF7 event target.
 *
 * @param {EventTarget|null} target Event target.
 * @return {HTMLElement|null}
 */
function getNewsletterWrap( target ) {
	if ( ! ( target instanceof Element ) ) {
		return null;
	}

	const wrap = target.closest( '[data-alteia-newsletter-cf7]' );

	return wrap instanceof HTMLElement ? wrap : null;
}

/**
 * Bind CF7 DOM events for a single newsletter wrapper.
 *
 * @param {HTMLElement} wrap Wrapper with [data-alteia-newsletter-cf7].
 */
function bindCf7Wrap( wrap ) {
	if ( boundWraps.has( wrap ) || wrap.dataset.alteiaCf7Bound === '1' ) {
		return;
	}

	boundWraps.add( wrap );
	wrap.dataset.alteiaCf7Bound = '1';

	const form = wrap.querySelector( '.wpcf7-form' );

	if ( ! form ) {
		return;
	}

	stabilizeSubmitWidth( wrap );

	// Capture phase so we can block double-submit / cooldown before CF7's handler.
	form.addEventListener(
		'submit',
		( event ) => {
			if (
				wrap.classList.contains( 'is-cooling-down' ) ||
				wrap.classList.contains( 'is-submitting' )
			) {
				event.preventDefault();
				event.stopImmediatePropagation();
				return;
			}

			setBusy( wrap, true );
			wrap.classList.remove( 'is-invalid', 'is-sent' );
			setCooldownMessage( wrap, '' );
		},
		true
	);

	const email = form.querySelector(
		'input[type="email"], input.wpcf7-email, input.wpcf7-text'
	);

	if ( email ) {
		email.addEventListener( 'input', () => {
			if ( wrap.classList.contains( 'is-invalid' ) ) {
				wrap.classList.remove( 'is-invalid' );
			}
		} );
	}
}

/**
 * Document-level CF7 listeners so every form instance resets independently.
 *
 * Contact Form 7 dispatches these on the form element; bubbling reaches document.
 */
function bindDocumentCf7Events() {
	if ( document.documentElement.dataset.alteiaNewsletterCf7Doc === '1' ) {
		return;
	}

	document.documentElement.dataset.alteiaNewsletterCf7Doc = '1';

	const resetBusy = ( event ) => {
		const wrap = getNewsletterWrap( event.target );
		if ( wrap ) {
			setBusy( wrap, false );
		}
	};

	document.addEventListener( 'wpcf7submit', resetBusy );
	document.addEventListener( 'wpcf7invalid', ( event ) => {
		const wrap = getNewsletterWrap( event.target );
		if ( ! wrap ) {
			return;
		}
		setBusy( wrap, false );
		wrap.classList.add( 'is-invalid' );
		wrap.classList.remove( 'is-sent' );
	} );
	document.addEventListener( 'wpcf7spam', ( event ) => {
		const wrap = getNewsletterWrap( event.target );
		if ( ! wrap ) {
			return;
		}
		setBusy( wrap, false );
		wrap.classList.add( 'is-invalid' );
		wrap.classList.remove( 'is-sent' );
	} );
	document.addEventListener( 'wpcf7mailfailed', ( event ) => {
		const wrap = getNewsletterWrap( event.target );
		if ( ! wrap ) {
			return;
		}
		setBusy( wrap, false );
		wrap.classList.add( 'is-invalid' );
		wrap.classList.remove( 'is-sent' );
	} );
	document.addEventListener( 'wpcf7mailsent', ( event ) => {
		const wrap = getNewsletterWrap( event.target );
		if ( ! wrap ) {
			return;
		}
		setBusy( wrap, false );
		wrap.classList.remove( 'is-invalid' );
		wrap.classList.add( 'is-sent' );
		startCooldown( wrap );
	} );
}

/**
 * Initialize CF7 newsletter wrappers.
 *
 * @param {ParentNode} root Root node.
 */
export function initNewsletterCf7( root = document ) {
	bindDocumentCf7Events();

	root.querySelectorAll( '[data-alteia-newsletter-cf7]' ).forEach(
		( wrap ) => {
			if ( wrap instanceof HTMLElement ) {
				bindCf7Wrap( wrap );
			}
		}
	);
}

/**
 * @deprecated Preview-only static forms are no longer submitted on the front end.
 * Kept as a no-op export for backwards-compatible imports.
 *
 * @param {ParentNode} root Root node.
 */
export function initNewsletterForms( root = document ) {
	initNewsletterCf7( root );
}
